import { FormErrorSummary, TextAreaInput, useForm, z, zodResolver } from '@tinycld/core/ui/form'
import { useCallback, useState } from 'react'
import type { NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native'
import { Pressable, Text, View } from 'react-native'
import { MentionInput, type MentionSuggestion } from './MentionInput'

const composerSchema = z.object({
    body: z.string().trim().min(1, 'Required').max(4000),
})

type ComposerValues = z.infer<typeof composerSchema>

export interface CommentComposerProps {
    placeholder?: string
    submitLabel: string
    isPending?: boolean
    error?: string | null
    autoFocus?: boolean
    onCancel?: () => void
    onSubmit: (body: string) => void
    // When supplied, the composer renders a MentionInput instead of a
    // plain TextAreaInput. The body still flows through the same form
    // value, so onSubmit's signature is unchanged — the only thing
    // that changes is that `[[@id]]` tokens may be embedded in the
    // submitted string.
    mentionSuggestions?: MentionSuggestion[]
    // A search hook, passed in as a prop, called with the text the user
    // has typed after `@` (empty string when no trigger is active). The
    // rows it returns are the popover's candidates.
    //
    // A hook as a prop is unusual, so: the composer owns the mention
    // trigger query but not the candidate pool — that belongs to the
    // package (text and calc search the user roster through core's
    // bounded `useMentionCandidates`; another surface might search a
    // project's members). The pool therefore has to be fetched by the
    // caller's hook, which means it has to run under a query the
    // composer holds. Handing the hook down is the only shape that
    // keeps the caller's hook at a component's top level, called
    // unconditionally on every render.
    //
    // The prop identity MUST be stable for the composer's lifetime —
    // pass a module-level hook function, never an inline closure, or
    // React sees a different hook at the same position each render.
    //
    // Supplying it also switches mentions on, exactly as
    // `mentionSuggestions` does, and takes precedence over it for the
    // picker: the search knows the query, the static list does not.
    // `mentionSuggestions` therefore only matters to a caller that has
    // a static pool and no search — read-mode display names are not
    // its job and never were: CommentThread resolves those from
    // `useMentionNames`.
    useMentionSuggestions?: (query: string) => MentionSuggestion[]
    // submitOnEnter switches the composer into chat-style mode:
    // Enter (without Shift) submits the form; Shift+Enter inserts a
    // newline. The visible submit button is hidden — Enter IS the
    // affordance. Used by the suggestion-thread reply composer to
    // match the Google-Docs interaction model where a tiny inline
    // input doesn't warrant a separate Reply button. The Cancel
    // affordance (when onCancel is supplied) still renders.
    submitOnEnter?: boolean
}

// Form composer used inside the drawer (new thread / reply) and any
// other comments surface in the future. Owns its own form state; the
// caller passes raw onSubmit(body) and gets isPending/error feedback in.
// Mentions activate when the caller supplies `mentionSuggestions`.
export function CommentComposer(props: CommentComposerProps) {
    const {
        control,
        handleSubmit,
        reset,
        formState: { errors, isSubmitted },
    } = useForm<ComposerValues>({
        resolver: zodResolver(composerSchema),
        defaultValues: { body: '' },
        mode: 'onChange',
    })

    const onSubmit = useCallback(
        () =>
            handleSubmit(values => {
                props.onSubmit(values.body)
                reset({ body: '' })
            })(),
        [handleSubmit, props.onSubmit, reset]
    )

    // Chat-style key handling for submitOnEnter mode. Enter without
    // shift submits; Shift+Enter falls through to the default newline
    // insertion. We can't easily inspect "is the mention dropdown
    // open" from out here, so a user who hits Enter while the picker
    // is up will both submit AND pick whatever's highlighted — for v1
    // that's acceptable. The picker is click-only today (no keyboard
    // nav), so the typical flow is: type @, click picker, then press
    // Enter to send.
    const onKeyPress = useCallback(
        (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
            if (!props.submitOnEnter) return
            const key = e.nativeEvent.key
            // Detect Shift via the underlying DOM event (web). On
            // native there's no Shift modifier in this event, so an
            // Enter on a hardware keyboard would always submit; for
            // the typical native chat case (mobile keyboard with a
            // dedicated "send" button) that's a feature, not a bug.
            const webEvent = e.nativeEvent as unknown as {
                shiftKey?: boolean
                preventDefault?: () => void
            }
            if (key === 'Enter' && !webEvent.shiftKey) {
                webEvent.preventDefault?.()
                onSubmit()
            }
        },
        [props.submitOnEnter, onSubmit]
    )

    // The live `@…` query MentionInput reports. Local, synchronous UI
    // state that nothing outside this composer needs.
    const [mentionQuery, setMentionQuery] = useState('')

    // Called unconditionally — see the prop's doc comment on why a hook
    // arrives as a prop and why its identity has to be stable. The
    // no-op fallback keeps the hook count identical between a caller
    // that passes one and a caller that doesn't, for a given mount.
    const searchedSuggestions = (props.useMentionSuggestions ?? useNoSuggestions)(mentionQuery)

    const onMentionQueryChange = useCallback((query: string | null) => {
        setMentionQuery(query ?? '')
    }, [])

    const mentionPool = props.useMentionSuggestions
        ? searchedSuggestions
        : (props.mentionSuggestions ?? EMPTY_SUGGESTIONS)

    const useMentions =
        props.mentionSuggestions !== undefined || props.useMentionSuggestions !== undefined

    return (
        <View>
            <FormErrorSummary errors={errors} isEnabled={isSubmitted} />
            {props.error ? <Text className="text-xs text-danger mb-2">{props.error}</Text> : null}
            {useMentions ? (
                <MentionInput
                    control={control}
                    name="body"
                    placeholder={props.placeholder}
                    autoFocus={props.autoFocus}
                    numberOfLines={3}
                    suggestions={mentionPool}
                    onQueryChange={onMentionQueryChange}
                    onKeyPress={props.submitOnEnter ? onKeyPress : undefined}
                />
            ) : (
                <TextAreaInput
                    control={control}
                    name="body"
                    placeholder={props.placeholder}
                    autoFocus={props.autoFocus}
                    numberOfLines={3}
                    onKeyPress={props.submitOnEnter ? onKeyPress : undefined}
                />
            )}
            {/* Submit button is hidden in submitOnEnter mode — Enter is the */}
            {/* affordance. Cancel still renders so the user can dismiss the */}
            {/* row without sending. */}
            {(!props.submitOnEnter || props.onCancel) && (
                <View className="flex-row justify-end gap-2 mt-2">
                    {props.onCancel ? (
                        <Pressable
                            accessibilityRole="button"
                            onPress={props.onCancel}
                            accessibilityLabel="Cancel comment"
                            className="px-3 py-1.5 rounded-md"
                        >
                            <Text className="text-xs font-semibold text-muted-foreground">
                                Cancel
                            </Text>
                        </Pressable>
                    ) : null}
                    {!props.submitOnEnter ? (
                        <Pressable
                            accessibilityRole="button"
                            onPress={onSubmit}
                            accessibilityLabel={props.submitLabel}
                            className="px-3 py-1.5 rounded-md bg-primary"
                            disabled={props.isPending}
                            style={{ opacity: props.isPending ? 0.6 : 1 }}
                        >
                            <Text className="text-xs font-semibold text-primary-foreground">
                                {props.submitLabel}
                            </Text>
                        </Pressable>
                    ) : null}
                </View>
            )}
        </View>
    )
}

// Stand-in for an absent `useMentionSuggestions` prop. A module-level
// function so its identity is stable, and a real (if trivial) hook
// position so the hook order never shifts.
function useNoSuggestions(_query: string): MentionSuggestion[] {
    return EMPTY_SUGGESTIONS
}

const EMPTY_SUGGESTIONS: MentionSuggestion[] = []
